/**
 * MCP Validation Tools
 *
 * Tools for validating IDF models and getting fix suggestions.
 */

import type { MCPTool } from '../types';

/**
 * Validate the entire model
 */
const validateModel: MCPTool = {
  name: 'validate_model',
  description: `Run full validation on the IDF model including schema validation, reference checking, and semantic checks. Returns errors and warnings.`,
  inputSchema: {
    type: 'object',
    properties: {
      includeWarnings: {
        type: 'boolean',
        description: 'Whether to include warnings in results (default: true)',
        default: true,
      },
    },
  },
};

/**
 * Validate a specific object
 */
const validateObject: MCPTool = {
  name: 'validate_object',
  description: `Validate a specific object against the IDD schema. Returns detailed validation results including field-level errors.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID to validate',
      },
    },
    required: ['objectId'],
  },
};

/**
 * Run sanity checks
 */
const runSanityChecks: MCPTool = {
  name: 'run_sanity_checks',
  description: `Run intelligent sanity checks on the model to detect common modeling mistakes, missing objects, unrealistic values, and best practice violations.`,
  inputSchema: {
    type: 'object',
    properties: {
      checkIds: {
        type: 'array',
        description: 'Optional: specific check IDs to run. If not specified, runs all checks.',
        items: {
          type: 'string',
        },
      },
    },
  },
};

/**
 * Get available auto-fixes
 */
const getAvailableAutoFixes: MCPTool = {
  name: 'get_available_auto_fixes',
  description: `Get a list of auto-fix operations that can be applied to resolve validation errors and sanity check warnings.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Check field value validity
 */
const checkFieldValue: MCPTool = {
  name: 'check_field_value',
  description: `Check if a value would be valid for a specific field before setting it. Returns validation result and suggestions.`,
  inputSchema: {
    type: 'object',
    properties: {
      className: {
        type: 'string',
        description: 'The object class name',
      },
      fieldIndex: {
        type: 'number',
        description: 'The field index',
      },
      value: {
        type: 'string',
        description: 'The value to validate',
      },
    },
    required: ['className', 'fieldIndex', 'value'],
  },
};

/**
 * Check reference validity
 */
const checkReference: MCPTool = {
  name: 'check_reference',
  description: `Check if a reference to another object is valid. Verifies the referenced object exists and is of the correct type.`,
  inputSchema: {
    type: 'object',
    properties: {
      referenceName: {
        type: 'string',
        description: 'The name being referenced',
      },
      expectedType: {
        type: 'string',
        description: 'Optional: the expected object type of the reference',
      },
    },
    required: ['referenceName'],
  },
};

/**
 * Get missing required objects
 */
const getMissingRequiredObjects: MCPTool = {
  name: 'get_missing_required_objects',
  description: `Get a list of required EnergyPlus objects that are missing from the model (e.g., Building, Version, SimulationControl).`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Analyze model completeness
 */
const analyzeModelCompleteness: MCPTool = {
  name: 'analyze_model_completeness',
  description: `Analyze the model for completeness and simulation readiness. Checks for essential components like weather, run period, output requests, etc.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Get suggested values
 */
const getSuggestedValues: MCPTool = {
  name: 'get_suggested_values',
  description: `Get suggested values for a field based on the context, typical values, and related objects in the model.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID',
      },
      fieldIndex: {
        type: 'number',
        description: 'The field index',
      },
    },
    required: ['objectId', 'fieldIndex'],
  },
};

/**
 * Preview changes
 */
const previewChanges: MCPTool = {
  name: 'preview_changes',
  description: `Preview how proposed changes would affect the model. Shows validation results and potential issues before applying changes.`,
  inputSchema: {
    type: 'object',
    properties: {
      changes: {
        type: 'array',
        description: 'Array of proposed changes to preview',
        items: {
          type: 'object',
          properties: {
            type: {
              type: 'string',
              enum: ['add', 'modify', 'remove'],
            },
            objectId: {
              type: 'string',
            },
            className: {
              type: 'string',
            },
            fieldIndex: {
              type: 'number',
            },
            value: {
              type: 'string',
            },
          },
        },
      },
    },
    required: ['changes'],
  },
};

export const validationTools: MCPTool[] = [
  validateModel,
  validateObject,
  runSanityChecks,
  getAvailableAutoFixes,
  checkFieldValue,
  checkReference,
  getMissingRequiredObjects,
  analyzeModelCompleteness,
  getSuggestedValues,
  previewChanges,
];
